/**
 * Structured logging utility to replace console.log statements
 * Provides different log levels and structured data
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  component?: string;
  userId?: string;
  terraceId?: string;
  timestamp?: Date;
  [key: string]: any;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  context?: LogContext;
  error?: Error;
}

class Logger {
  private isDevelopment = import.meta.env.DEV;
  private logHistory: LogEntry[] = [];
  private maxHistorySize = 100;

  private createLogEntry(level: LogLevel, message: string, context?: LogContext, error?: Error): LogEntry {
    const entry: LogEntry = {
      level,
      message,
      context: {
        ...context,
        timestamp: new Date(),
      },
      error,
    };

    // Store in history (limited size)
    this.logHistory.push(entry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }

    return entry;
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.isDevelopment) {
      // In production, only log warnings and errors
      return level === 'warn' || level === 'error';
    }
    return true;
  }

  private formatMessage(entry: LogEntry): string {
    const timestamp = entry.context?.timestamp?.toISOString() || '';
    const component = entry.context?.component ? `[${entry.context.component}]` : '';
    return `${timestamp} ${component} ${entry.message}`;
  }

  debug(message: string, context?: LogContext): void {
    if (!this.shouldLog('debug')) return;
    
    const entry = this.createLogEntry('debug', message, context);
    if (this.isDevelopment) {
      console.debug(this.formatMessage(entry), context);
    }
  }

  info(message: string, context?: LogContext): void {
    if (!this.shouldLog('info')) return;
    
    const entry = this.createLogEntry('info', message, context);
    if (this.isDevelopment) {
      console.info(this.formatMessage(entry), context);
    }
  }

  warn(message: string, context?: LogContext): void {
    if (!this.shouldLog('warn')) return;
    
    const entry = this.createLogEntry('warn', message, context);
    console.warn(this.formatMessage(entry), context);
  }

  error(message: string, context?: LogContext, error?: Error): void {
    const entry = this.createLogEntry('error', message, context, error);
    console.error(this.formatMessage(entry), { context, error });
    
    // In production, you might want to send to error reporting service
    if (!this.isDevelopment && error) {
      this.reportError(entry);
    }
  }

  // Component-specific logger factory
  forComponent(componentName: string) {
    return {
      debug: (message: string, context?: Omit<LogContext, 'component'>) =>
        this.debug(message, { ...context, component: componentName }),
      info: (message: string, context?: Omit<LogContext, 'component'>) =>
        this.info(message, { ...context, component: componentName }),
      warn: (message: string, context?: Omit<LogContext, 'component'>) =>
        this.warn(message, { ...context, component: componentName }),
      error: (message: string, context?: Omit<LogContext, 'component'>, error?: Error) =>
        this.error(message, { ...context, component: componentName }, error),
    };
  }

  // Get log history for debugging
  getHistory(): LogEntry[] {
    return [...this.logHistory];
  }

  // Clear history
  clearHistory(): void {
    this.logHistory = [];
  }

  private reportError(entry: LogEntry): void {
    // Placeholder for error reporting service integration
    // Could integrate with Sentry, LogRocket, etc.
  }
}

// Export singleton instance
export const logger = new Logger();

// Export convenience functions
export const debugLog = logger.debug.bind(logger);
export const infoLog = logger.info.bind(logger);
export const warnLog = logger.warn.bind(logger);
export const errorLog = logger.error.bind(logger);